/**
 * The two world forms, on the welcome card and in the hub: join a world by its code, and name and
 * create a new one, its name suggested ("Ann's world": an empty field takes the suggestion). They
 * ask the lobby (the net layer answers through the store); a code left empty says so through
 * `onHint` instead.
 */
import { useState, type JSX } from 'react';

import { displayCode, normalizeCode, requestLobby } from '@game/session/lobby';
import { t } from '@i18n/index';
import { useGame, useGameState } from '@ui/hooks';

const clean = (value: string): string => value.replace(/\s+/g, ' ').trim().slice(0, 24);

type Hint = (hint: string | null) => void;

export function JoinForm({ onHint }: { onHint: Hint }): JSX.Element {
  const game = useGame();
  const busy = useGameState((s) => s.lobby.busy);
  const [code, setCode] = useState('');
  return (
    <form
      className="form"
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        const v = normalizeCode(code);
        if (!v) {
          onHint(t('welcome.code-first'));
          return;
        }
        onHint(null);
        requestLobby(game, { kind: 'join', code: v });
      }}
    >
      <input
        id="world-code"
        className="code"
        maxLength={9}
        placeholder={t('welcome.code-placeholder')}
        aria-label={t('welcome.code-aria')}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        value={displayCode(normalizeCode(code))}
        onChange={(e) => setCode(e.target.value)}
        disabled={busy}
      />
      <button className="btn primary" type="submit" disabled={busy}>
        {t('welcome.join-btn')}
      </button>
    </form>
  );
}

export function CreateForm({ onHint }: { onHint: Hint }): JSX.Element {
  const game = useGame();
  const busy = useGameState((s) => s.lobby.busy);
  const me = useGameState((s) => s.me);
  const [name, setName] = useState('');
  const suggestion = me ? t('world.first-name', { name: me.name }) : '';
  return (
    <form
      className="form"
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        const v = clean(name) || clean(suggestion);
        if (!v) {
          onHint(t('welcome.name-first'));
          return;
        }
        onHint(null);
        requestLobby(game, { kind: 'create', name: v });
      }}
    >
      <input
        id="world-name"
        maxLength={24}
        placeholder={suggestion || t('welcome.name-placeholder')}
        aria-label={t('welcome.name-aria')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={busy}
      />
      <button className="btn primary" type="submit" disabled={busy}>
        {t('welcome.create-btn')}
      </button>
    </form>
  );
}
